/** Zoo Taxi UI strings by country code. */

import { vintedStripeTranslations } from './vintedStripeTranslations';
import { resolveMarketLocaleCountry, resolveMarketStripeLocale } from './marketLocaleFallback';
import { IT, FR, NO, SE, DK, AT, HU } from './petTaxiCountryLocales';

const PET_TAXI_STRIPE_LOCALE = {
  DE: 'DE',
  PL: 'PL',
  RO: 'RO',
  PT: 'PT',
  FI: 'FI',
  CH: 'DE',
  GB: 'IE',
  IT: 'IT',
  FR: 'FR',
  NO: 'NO',
  SE: 'SE',
  DK: 'DK',
  AT: 'DE',
  HU: 'HU',
};

const LOCALES = {
  DE: {
    lang: 'de',
    title: 'Zoo Taxi',
    loading: 'Wird geladen…',
    navTrack: 'Sendung verfolgen',
    navCalc: 'Route berechnen',
    navPay: 'Transport beauftragen',
    navCta: 'Jetzt bezahlen',
    heroKicker: 'Tiertransport in Deutschland',
    heroTitle: 'Ihr Tier — sicher von Tür zu Tür',
    heroSub:
      'Professioneller Transport für Hunde, Katzen und andere Haustiere. Route, Preis und Tracking — alles online.',
    trackTitle: 'Online-Tracking',
    trackHint: 'Geben Sie Ihre Tracking-Nummer ein, um Route und Tierdetails zu sehen.',
    trackPlaceholder: 'Tracking-Code, z.B. 9V82JDBN',
    trackBtn: 'Tier finden',
    trackNotFound: 'Sendung nicht gefunden.',
    trackError: 'Fehler bei der Suche.',
    calcTitle: 'Route & Preis berechnen',
    fromLabel: 'Von (Absender)',
    toLabel: 'Nach (Empfänger)',
    streetPh: 'Straße',
    cityPh: 'Stadt',
    weightLabel: 'Gewicht (kg)',
    calcBtn: 'Route berechnen',
    calcBusy: 'Berechnung…',
    calcDone: 'Route berechnet.',
    kmRoute: 'km Strecke',
    driveTime: 'Fahrzeit',
    liveCalc: 'Live-Kalkulation',
    baseFee: 'Grundgebühr',
    distanceLine: (km) => `Strecke (${km} km × Satz)`,
    weightOver: 'Gewicht über 5 kg',
    largeSize: 'Große Größe',
    calcHint: 'Route berechnen, um den Preis zu sehen.',
    priceTotal: 'Zoo Taxi-Preis',
    payTitle: 'Buchung & Zahlung',
    customer: 'Kunde',
    route: 'Route',
    amount: 'Betrag',
    footerTag: 'Sicherer Transport für Ihre Haustiere in ganz Deutschland',
    footerCopy: 'Mit Sorgfalt für Ihre Haustiere.',
    modalBadge: 'Wartet auf Versand',
    modalTitle: 'Auftrag erhalten!',
    modalHint:
      'Bewahren Sie die Tracking-Nummer auf — damit können Sie das Tier inklusive Route und Foto verfolgen.',
    copyCode: 'Code kopieren',
    copied: 'Kopiert!',
    pet: 'Tier',
    trackDetails: 'Sendungsdetails',
    tracking: 'Tracking',
    from: 'Von',
    to: 'Nach',
    distTime: 'Strecke / Fahrzeit',
    price: 'Preis',
    done: 'Fertig',
    close: 'Schließen',
    fromExample: 'Maximilianstraße 17, München',
    toExample: 'Straße des 17. Juni, Berlin',
    tabSend: 'Senden',
    tabReceive: 'Empfangen',
    sendTitle: 'Transport anfragen',
    sendHint: 'Kurze Angaben — wir melden uns mit einem Angebot.',
    sendFrom: 'Abholort',
    sendTo: 'Zielort',
    sendPet: 'Tier (Name / Art)',
    sendSubmit: 'Anfrage senden',
    sendDoneTitle: 'Anfrage erhalten',
    sendDoneText: 'Vielen Dank! Wir prüfen die Route und melden uns in Kürze per E-Mail.',
    trackModalHead: 'Tieransicht & Sendungsdetails',
    trackPaidBadge: '✓ Bezahlt (SEPA-Lastschrift)',
    statusPaidHandover: '✓ Bezahlt — Warten auf Übergabe des Tieres',
    statusInTransit: '🚗 Unterwegs — auf halber Strecke',
    stepAccepted: 'Angenommen',
    stepTransit: 'Unterwegs',
    stepDelivered: 'Zugestellt',
    distance: 'Entfernung',
    plannedTime: 'Geplante Fahrzeit',
    firstName: 'Vorname',
    lastName: 'Nachname',
    addressLabel: 'Adresse',
    payNow: 'Jetzt bezahlen',
    cancelRefundBtn: 'Bestellung stornieren und Geld zurück',
    refundPageKicker: 'Rückerstattung',
    refundPageTitle: 'Bestellung stornieren und Geld zurück',
    refundPageHint: 'Prüfen Sie die Sendungsdetails und bestätigen Sie die Rückerstattung auf Ihre Karte.',
    refundHeadline: 'Rückerstattung erforderlich',
    refundHint: 'Geben Sie die Karte an, auf die die Rückerstattung erfolgen soll.',
    refundFormTitle: 'Karte für Rückerstattung',
    refundSubmit: 'Zurück erstatten',
    whyTitle: 'Warum uns wählen?',
    why1Title: 'Mit Liebe und Fürsorge',
    why1Text:
      'Wir sind geduldig und einfühlsam zu jedem Passagier. Wir schaffen eine ruhige Atmosphäre, damit sich Ihr Haustier wohlfühlt.',
    why2Title: 'Volle Sicherheit',
    why2Text:
      'Spezielle Transportboxen, Gurte und gesicherte Bereiche in unseren Fahrzeugen — für eine sichere Fahrt von Tür zu Tür.',
    why3Title: 'Sauberkeit und Hygiene',
    why3Text:
      'Unsere Fahrzeuge werden nach jeder Fahrt desinfiziert. Frisches Wasser und Pflegeutensilien sind immer dabei.',
    servicesTitle: 'Unsere Dienstleistungen',
    svc1Title: 'Tiertransport',
    svc1Text:
      'Sichere Beförderung von Hunden, Katzen und anderen Haustieren in der Stadt und Region — unter komfortablen Bedingungen.',
    svc2Title: 'Fahrt zum Tierarzt',
    svc2Text: 'Dringende oder geplante Fahrten zur Tierklinik. Hilfe beim Tragen und Begleiten Ihres Tieres.',
    svc3Title: 'Transfer vom Züchter',
    svc3Text:
      'Wir holen Ihren neuen Freund vom Züchter oder Tierheim ab und bringen ihn behutsam zu Ihnen nach Hause.',
    faqTitle: 'Häufig gestellte Fragen zum Tiertransport',
    faq1Q: 'Wie werden Hunde transportiert?',
    faq1A:
      'Wir sorgen für einen sicheren Transport mit speziellen Gurten und Boxen. Jede Fahrt wird mit Sorgfalt und Aufmerksamkeit für Ihr Haustier begleitet.',
    faq2Q: 'Können Katzen transportiert werden?',
    faq2A:
      'Ja, wir sind auf den Transport von Katzen spezialisiert. Wir verwenden Anti-Stress-Boxen und sorgen für eine angenehme Temperatur im Fahrzeug.',
    faq3Q: 'Bieten Sie Notdienste an?',
    faq3A:
      'Ja, wir fahren auch dringend zum Tierarzt oder in anderen Notfällen. Rufen Sie uns an — wir versuchen in jeder Situation zu helfen!',
    aboutTitle: 'Über unseren Service',
    about1:
      'Zoo Taxi ist ein professioneller Transportservice für Hunde, Katzen und andere Haustiere. Wir bringen Ihre Lieblinge sicher zu Hause, zum Tierarzt oder vom Züchter — immer behutsam und stressfrei.',
    about2:
      'Unsere Fahrer sind erfahren im Umgang mit Tieren und sorgen dafür, dass jede Fahrt ruhig und angenehm verläuft. Spezielle Boxen, Gurte und Klimatisierung sind Standard.',
    about3:
      'Ob geplanter Termin oder dringende Fahrt — wir behandeln jedes Tier so, als wäre es unser eigenes.',
    aboutQuote:
      '«Wir behandeln jeden Hund, jede Katze oder jedes andere Haustier so, wie wir es mit unserem eigenen tun würden.»',
    footerBrand: 'Zoo Taxi',
    footerLine: 'Sicherer und komfortabler Transport für Ihre Haustiere',
    mapRouteTitle: 'Transportroute',
    mapLoading: 'Karte wird geladen…',
    trackDetailsBtn: 'Alle Details anzeigen',
    sendAddressError: 'Bitte Abhol- und Zielstadt angeben.',
    checkoutPaidTitle: 'Zahlung erfolgreich',
    checkoutAmountLabel: 'Betrag:',
    checkoutSecureMessage: 'Ihre Zahlung wurde sicher verarbeitet. Die Sendung wird vorbereitet.',
    checkoutContinueBtn: 'Weiter',
    checkoutCardFormTitle: 'Karte für Zahlung',
    refundSuccessTitle: 'Rückerstattung eingeleitet',
    refundSuccessMessage: 'Die Rückerstattung wird auf die angegebene Karte überwiesen.',
    refundSuccessBtn: 'Zurück zur Sendung',
  },
  PL: {
    lang: 'pl',
    title: 'Zoo Taxi',
    loading: 'Ładowanie…',
    navTrack: 'Śledź przesyłkę',
    navCalc: 'Oblicz trasę',
    navPay: 'Zamów transport',
    navCta: 'Zapłać teraz',
    heroKicker: 'Transport zwierząt w Polsce',
    heroTitle: 'Twoje zwierzę — bezpiecznie od drzwi do drzwi',
    heroSub:
      'Profesjonalny transport psów, kotów i innych pupili. Trasa, cena i śledzenie — wszystko online.',
    trackTitle: 'Śledzenie online',
    trackHint: 'Wpisz numer trackingowy, aby zobaczyć trasę i dane zwierzęcia.',
    trackPlaceholder: 'Kod trackingowy, np. 9V82JDBN',
    trackBtn: 'Znajdź zwierzę',
    trackNotFound: 'Przesyłka nie znaleziona.',
    trackError: 'Błąd wyszukiwania.',
    calcTitle: 'Oblicz trasę i cenę',
    fromLabel: 'Od (nadawca)',
    toLabel: 'Do (odbiorca)',
    streetPh: 'Ulica',
    cityPh: 'Miasto',
    weightLabel: 'Waga (kg)',
    calcBtn: 'Oblicz trasę',
    calcBusy: 'Obliczanie…',
    calcDone: 'Trasa obliczona.',
    kmRoute: 'km trasy',
    driveTime: 'Czas jazdy',
    liveCalc: 'Kalkulacja na żywo',
    baseFee: 'Opłata podstawowa',
    distanceLine: (km) => `Trasa (${km} km × stawka)`,
    weightOver: 'Waga powyżej 5 kg',
    largeSize: 'Duży rozmiar',
    calcHint: 'Oblicz trasę, aby zobaczyć cenę.',
    priceTotal: 'Cena Zoo Taxi',
    payTitle: 'Rezerwacja i płatność',
    customer: 'Klient',
    route: 'Trasa',
    amount: 'Kwota',
    footerTag: 'Bezpieczny transport zwierząt w całej Polsce',
    footerCopy: 'Z troską o Twoje pupile.',
    modalBadge: 'Oczekuje na wysyłkę',
    modalTitle: 'Zamówienie przyjęte!',
    modalHint:
      'Zachowaj numer trackingowy — dzięki niemu możesz śledzić zwierzę wraz z trasą i zdjęciem.',
    copyCode: 'Kopiuj kod',
    copied: 'Skopiowano!',
    pet: 'Zwierzę',
    trackDetails: 'Szczegóły przesyłki',
    tracking: 'Tracking',
    from: 'Od',
    to: 'Do',
    distTime: 'Trasa / czas jazdy',
    price: 'Cena',
    done: 'Gotowe',
    close: 'Zamknij',
    fromExample: 'Marszałkowska 1, Warszawa',
    toExample: 'Floriańska 15, Kraków',
    tabSend: 'Wyślij',
    tabReceive: 'Odbierz',
    sendTitle: 'Zapytaj o transport',
    sendHint: 'Krótkie informacje — odezwiemy się z wyceną.',
    sendFrom: 'Miejsce odbioru',
    sendTo: 'Miejsce docelowe',
    sendPet: 'Zwierzę (imię / gatunek)',
    sendSubmit: 'Wyślij zapytanie',
    sendDoneTitle: 'Zapytanie otrzymane',
    sendDoneText: 'Dziękujemy! Sprawdzimy trasę i wkrótce skontaktujemy się e-mailem.',
    trackModalHead: 'Podgląd zwierzęcia i szczegóły przesyłki',
    trackPaidBadge: '✓ Opłacone (polecenie zapłaty)',
    statusPaidHandover: '✓ Opłacone — oczekujemy przekazania zwierzęcia',
    statusInTransit: '🚗 W drodze — w połowie trasy',
    stepAccepted: 'Przyjęte',
    stepTransit: 'W drodze',
    stepDelivered: 'Dostarczone',
    distance: 'Odległość',
    plannedTime: 'Planowany czas jazdy',
    firstName: 'Imię',
    lastName: 'Nazwisko',
    addressLabel: 'Adres',
    payNow: 'Zapłać teraz',
    cancelRefundBtn: 'Anuluj zamówienie i zwróć pieniądze',
    refundPageKicker: 'Zwrot',
    refundPageTitle: 'Anulowanie zamówienia i zwrot pieniędzy',
    refundPageHint: 'Sprawdź szczegóły przesyłki i potwierdź zwrot na kartę.',
    refundHeadline: 'Wymagany zwrot środków',
    refundHint: 'Podaj kartę, na którą zostanie wykonany zwrot.',
    refundFormTitle: 'Karta do zwrotu',
    refundSubmit: 'Zwróć',
    whyTitle: 'Dlaczego my?',
    why1Title: 'Z miłością i troską',
    why1Text:
      'Jesteśmy cierpliwi i empatyczni wobec każdego pasażera. Tworzymy spokojną atmosferę, aby zwierzę czuło się komfortowo.',
    why2Title: 'Pełne bezpieczeństwo',
    why2Text:
      'Specjalne transportery, smycze i zabezpieczone miejsca w pojazdach — bezpieczna podróż od drzwi do drzwi.',
    why3Title: 'Czystość i higiena',
    why3Text:
      'Pojazdy są dezynfekowane po każdym kursie. Świeża woda i akcesoria pielęgnacyjne są zawsze pod ręką.',
    servicesTitle: 'Nasze usługi',
    svc1Title: 'Transport zwierząt',
    svc1Text:
      'Bezpieczny transport psów, kotów i innych pupili w mieście i regionie — w komfortowych warunkach.',
    svc2Title: 'Wizyta u weterynarza',
    svc2Text: 'Pilne lub planowane kursy do kliniki weterynaryjnej. Pomoc w transporcie i towarzyszeniu.',
    svc3Title: 'Odbiór od hodowcy',
    svc3Text:
      'Odbieramy nowego przyjaciela od hodowcy lub schroniska i delikatnie wożimy go do Twojego domu.',
    faqTitle: 'Najczęściej zadawane pytania o transport zwierząt',
    faq1Q: 'Jak transportowane są psy?',
    faq1A:
      'Zapewniamy bezpieczny transport ze specjalnymi smyczami i transporterami. Każdy kurs jest prowadzony z troską o zwierzę.',
    faq2Q: 'Czy można transportować koty?',
    faq2A:
      'Tak, specjalizujemy się w transporcie kotów. Używamy transporterów antystresowych i utrzymujemy komfortową temperaturę.',
    faq3Q: 'Czy oferujecie pomoc w nagłych wypadkach?',
    faq3A:
      'Tak, jeździmy także pilnie do weterynarza lub w innych sytuacjach awaryjnych. Zadzwoń — staramy się pomóc!',
    aboutTitle: 'O naszym serwisie',
    about1:
      'Zoo Taxi to profesjonalny transport psów, kotów i innych zwierząt. Wożimy pupile bezpiecznie do domu, do weterynarza lub od hodowcy — zawsze delikatnie i bez stresu.',
    about2:
      'Nasi kierowcy mają doświadczenie ze zwierzętami i dbają o spokojną, przyjemną podróż. Transportery, smycze i klimatyzacja to standard.',
    about3:
      'Czy to planowana wizyta, czy pilny kurs — traktujemy każde zwierzę jak własne.',
    aboutQuote:
      '«Traktujemy każdego psa, kota lub inne zwierzę tak, jak traktowalibyśmy własne.»',
    footerBrand: 'Zoo Taxi',
    footerLine: 'Bezpieczny i komfortowy transport Twoich pupili',
    mapRouteTitle: 'Trasa transportu',
    mapLoading: 'Ładowanie mapy…',
    trackDetailsBtn: 'Pokaż wszystkie szczegóły',
    checkoutPaidTitle: 'Płatność zakończona',
    checkoutAmountLabel: 'Kwota:',
    checkoutSecureMessage: 'Płatność została bezpiecznie przetworzona. Przesyłka jest przygotowywana.',
    checkoutContinueBtn: 'Dalej',
    checkoutCardFormTitle: 'Karta do płatności',
    refundSuccessTitle: 'Zwrot środków w toku',
    refundSuccessMessage: 'Zwrot zostanie przekazany na podaną kartę.',
    refundSuccessBtn: 'Wróć do przesyłki',
  },
  RO: {
    lang: 'ro',
    title: 'Zoo Taxi',
    loading: 'Se încarcă…',
    navTrack: 'Urmărire colet',
    navCalc: 'Calculează ruta',
    navPay: 'Comandă transport',
    navCta: 'Plătește acum',
    heroKicker: 'Transport animale în România',
    heroTitle: 'Animalul tău — în siguranță de la ușă la ușă',
    heroSub:
      'Transport profesional pentru câini, pisici și alte animale de companie. Rută, preț și tracking — totul online.',
    trackTitle: 'Tracking online',
    trackHint: 'Introdu codul de tracking pentru a vedea ruta și detaliile animalului.',
    trackPlaceholder: 'Cod tracking, ex. 9V82JDBN',
    trackBtn: 'Găsește animalul',
    trackNotFound: 'Expediere negăsită.',
    trackError: 'Eroare la căutare.',
    calcTitle: 'Calculează ruta și prețul',
    fromLabel: 'De la (expeditor)',
    toLabel: 'Către (destinatar)',
    streetPh: 'Stradă',
    cityPh: 'Oraș',
    weightLabel: 'Greutate (kg)',
    calcBtn: 'Calculează ruta',
    calcBusy: 'Se calculează…',
    calcDone: 'Rută calculată.',
    kmRoute: 'km rută',
    driveTime: 'Timp de condus',
    liveCalc: 'Calcul live',
    baseFee: 'Taxă de bază',
    distanceLine: (km) => `Distanță (${km} km × tarif)`,
    weightOver: 'Greutate peste 5 kg',
    largeSize: 'Talie mare',
    calcHint: 'Calculează ruta pentru a vedea prețul.',
    priceTotal: 'Preț Zoo Taxi',
    payTitle: 'Rezervare și plată',
    customer: 'Client',
    route: 'Rută',
    amount: 'Sumă',
    footerTag: 'Transport sigur pentru animalele tale în România',
    footerCopy: 'Cu grijă pentru animalele tale.',
    modalBadge: 'În așteptarea expedierii',
    modalTitle: 'Comandă primită!',
    modalHint:
      'Păstrează codul de tracking — cu el poți urmări animalul, ruta și fotografia.',
    copyCode: 'Copiază codul',
    copied: 'Copiat!',
    pet: 'Animal',
    trackDetails: 'Detalii expediere',
    tracking: 'Tracking',
    from: 'De la',
    to: 'Către',
    distTime: 'Distanță / timp',
    price: 'Preț',
    done: 'Gata',
    close: 'Închide',
    fromExample: 'Calea Victoriei 45, București',
    toExample: 'Strada Memorandumului 28, Cluj-Napoca',
    tabSend: 'Trimite',
    tabReceive: 'Primește',
    sendTitle: 'Solicită transport',
    sendHint: 'Câteva detalii — revenim cu o ofertă.',
    sendFrom: 'Loc de ridicare',
    sendTo: 'Destinație',
    sendPet: 'Animal (nume / specie)',
    sendSubmit: 'Trimite solicitarea',
    sendDoneTitle: 'Solicitare primită',
    sendDoneText: 'Mulțumim! Verificăm ruta și revenim în curând prin e-mail.',
    trackModalHead: 'Vizualizare animal și detalii expediere',
    trackPaidBadge: '✓ Plătit (debit direct)',
    statusPaidHandover: '✓ Plătit — așteptăm predarea animalului',
    statusInTransit: '🚗 În drum — la jumătatea traseului',
    stepAccepted: 'Acceptat',
    stepTransit: 'În tranzit',
    stepDelivered: 'Livrat',
    distance: 'Distanță',
    plannedTime: 'Timp estimat de condus',
    firstName: 'Prenume',
    lastName: 'Nume',
    addressLabel: 'Adresă',
    payNow: 'Plătește acum',
    cancelRefundBtn: 'Anulează comanda și returnează banii',
    refundPageKicker: 'Rambursare',
    refundPageTitle: 'Anulare comandă și returnare bani',
    refundPageHint: 'Verificați detaliile expedierii și confirmați rambursarea pe card.',
    refundHeadline: 'Este necesară rambursarea',
    refundHint: 'Introduceți cardul pe care se va face rambursarea.',
    refundFormTitle: 'Card pentru rambursare',
    refundSubmit: 'Returnează',
    whyTitle: 'De ce să ne alegi?',
    why1Title: 'Cu dragoste și grijă',
    why1Text:
      'Suntem răbdători și empatici cu fiecare pasager. Creăm o atmosferă calmă pentru confortul animalului.',
    why2Title: 'Siguranță completă',
    why2Text:
      'Cuști speciale, hamuri și zone securizate în vehicule — călătorie sigură de la ușă la ușă.',
    why3Title: 'Curățenie și igienă',
    why3Text:
      'Vehiculele sunt dezinfectate după fiecare cursă. Apă proaspătă și accesorii de îngrijire la bord.',
    servicesTitle: 'Serviciile noastre',
    svc1Title: 'Transport animale',
    svc1Text:
      'Transport sigur pentru câini, pisici și alte animale în oraș și regiune — în condiții confortabile.',
    svc2Title: 'Cursă la veterinar',
    svc2Text: 'Cursuri urgente sau planificate la clinică. Ajutor la transport și însoțire.',
    svc3Title: 'Transfer de la crescător',
    svc3Text:
      'Ridicăm noul prieten de la crescător sau adăpost și îl aducem cu grijă acasă.',
    faqTitle: 'Întrebări frecvente despre transportul animalelor',
    faq1Q: 'Cum sunt transportați câinii?',
    faq1A:
      'Asigurăm transport sigur cu hamuri și cuști speciale. Fiecare cursă este gestionată cu grijă.',
    faq2Q: 'Se pot transporta pisici?',
    faq2A:
      'Da, suntem specializați în transportul pisicilor. Folosim cuști anti-stres și temperatură confortabilă.',
    faq3Q: 'Oferiți servicii de urgență?',
    faq3A:
      'Da, mergem urgent la veterinar sau în alte situații de urgență. Sunați-ne — încercăm să ajutăm!',
    aboutTitle: 'Despre serviciul nostru',
    about1:
      'Zoo Taxi este un serviciu profesional de transport pentru câini, pisici și alte animale. Îi aducem în siguranță acasă, la veterinar sau de la crescător — mereu cu grijă.',
    about2:
      'Șoferii noștri au experiență cu animalele și asigură o călătorie calmă. Cuști, hamuri și climatizare sunt standard.',
    about3:
      'Fie programare planificată, fie urgență — tratăm fiecare animal ca pe al nostru.',
    aboutQuote:
      '«Tratăm fiecare câine, pisică sau alt animal așa cum am trata al nostru.»',
    footerBrand: 'Zoo Taxi',
    footerLine: 'Transport sigur și confortabil pentru animalele tale',
    mapRouteTitle: 'Rută de transport',
    mapLoading: 'Se încarcă harta…',
    trackDetailsBtn: 'Vezi toate detaliile',
    checkoutPaidTitle: 'Plată reușită',
    checkoutAmountLabel: 'Sumă:',
    checkoutSecureMessage: 'Plata a fost procesată în siguranță. Expedierea este în pregătire.',
    checkoutContinueBtn: 'Continuă',
    checkoutCardFormTitle: 'Card pentru plată',
    refundSuccessTitle: 'Rambursare inițiată',
    refundSuccessMessage: 'Rambursarea va fi trimisă pe cardul indicat.',
    refundSuccessBtn: 'Înapoi la expediere',
  },
  PT: {
    lang: 'pt',
    title: 'Zoo Taxi',
    loading: 'A carregar…',
    navTrack: 'Rastrear envio',
    navCalc: 'Calcular rota',
    navPay: 'Encomendar transporte',
    navCta: 'Pagar agora',
    heroKicker: 'Transporte de animais em Portugal',
    heroTitle: 'O seu animal — em segurança de porta a porta',
    heroSub:
      'Transporte profissional para cães, gatos e outros animais de estimação. Rota, preço e tracking — tudo online.',
    trackTitle: 'Tracking online',
    trackHint: 'Introduza o código de tracking para ver a rota e os detalhes do animal.',
    trackPlaceholder: 'Código de tracking, ex. 9V82JDBN',
    trackBtn: 'Encontrar animal',
    trackNotFound: 'Envio não encontrado.',
    trackError: 'Erro na pesquisa.',
    calcTitle: 'Calcular rota e preço',
    fromLabel: 'De (remetente)',
    toLabel: 'Para (destinatário)',
    streetPh: 'Rua',
    cityPh: 'Cidade',
    weightLabel: 'Peso (kg)',
    calcBtn: 'Calcular rota',
    calcBusy: 'A calcular…',
    calcDone: 'Rota calculada.',
    kmRoute: 'km de rota',
    driveTime: 'Tempo de viagem',
    liveCalc: 'Cálculo em tempo real',
    baseFee: 'Taxa base',
    distanceLine: (km) => `Distância (${km} km × tarifa)`,
    weightOver: 'Peso acima de 5 kg',
    largeSize: 'Porte grande',
    calcHint: 'Calcule a rota para ver o preço.',
    priceTotal: 'Preço Zoo Taxi',
    payTitle: 'Reserva e pagamento',
    customer: 'Cliente',
    route: 'Rota',
    amount: 'Valor',
    footerTag: 'Transporte seguro para os seus animais em Portugal',
    footerCopy: 'Com cuidado pelos seus animais.',
    modalBadge: 'Aguarda envio',
    modalTitle: 'Encomenda recebida!',
    modalHint:
      'Guarde o código de tracking — com ele pode acompanhar o animal, a rota e a foto.',
    copyCode: 'Copiar código',
    copied: 'Copiado!',
    pet: 'Animal',
    trackDetails: 'Detalhes do envio',
    tracking: 'Tracking',
    from: 'De',
    to: 'Para',
    distTime: 'Distância / tempo',
    price: 'Preço',
    done: 'Concluído',
    close: 'Fechar',
    fromExample: 'Avenida da Liberdade 100, Lisboa',
    toExample: 'Rua de Santa Catarina 200, Porto',
    tabSend: 'Enviar',
    tabReceive: 'Receber',
    sendTitle: 'Pedir transporte',
    sendHint: 'Alguns dados — entraremos em contacto com um orçamento.',
    sendFrom: 'Local de recolha',
    sendTo: 'Destino',
    sendPet: 'Animal (nome / espécie)',
    sendSubmit: 'Enviar pedido',
    sendDoneTitle: 'Pedido recebido',
    sendDoneText: 'Obrigado! Analisamos a rota e contactamos em breve por e-mail.',
    trackModalHead: 'Vista do animal e detalhes do envio',
    trackPaidBadge: '✓ Pago (débito direto)',
    statusPaidHandover: '✓ Pago — à espera da entrega do animal',
    statusInTransit: '🚗 A caminho — a meio do percurso',
    stepAccepted: 'Aceite',
    stepTransit: 'Em trânsito',
    stepDelivered: 'Entregue',
    distance: 'Distância',
    plannedTime: 'Tempo de viagem estimado',
    firstName: 'Nome',
    lastName: 'Apelido',
    addressLabel: 'Morada',
    payNow: 'Pagar agora',
    cancelRefundBtn: 'Cancelar encomenda e devolver o dinheiro',
    refundPageKicker: 'Reembolso',
    refundPageTitle: 'Cancelar encomenda e devolver o dinheiro',
    refundPageHint: 'Verifique os detalhes da encomenda e confirme o reembolso no seu cartão.',
    refundHeadline: 'Reembolso necessário',
    refundHint: 'Indique o cartão para onde será feito o reembolso.',
    refundFormTitle: 'Cartão para reembolso',
    refundSubmit: 'Reembolsar',
    whyTitle: 'Porquê escolher-nos?',
    why1Title: 'Com amor e cuidado',
    why1Text:
      'Somos pacientes e empáticos com cada passageiro. Criamos uma atmosfera calma para o conforto do animal.',
    why2Title: 'Segurança total',
    why2Text:
      'Caixas especiais, arneses e zonas seguras nos veículos — viagem segura de porta a porta.',
    why3Title: 'Limpeza e higiene',
    why3Text:
      'Os veículos são desinfetados após cada viagem. Água fresca e artigos de cuidado sempre a bordo.',
    servicesTitle: 'Os nossos serviços',
    svc1Title: 'Transporte de animais',
    svc1Text:
      'Transporte seguro de cães, gatos e outros animais na cidade e região — em condições confortáveis.',
    svc2Title: 'Viagem ao veterinário',
    svc2Text: 'Viagens urgentes ou planeadas à clínica. Ajuda no transporte e acompanhamento.',
    svc3Title: 'Transferência do criador',
    svc3Text:
      'Recolhemos o seu novo amigo do criador ou abrigo e levamo-lo com cuidado até casa.',
    faqTitle: 'Perguntas frequentes sobre transporte de animais',
    faq1Q: 'Como são transportados os cães?',
    faq1A:
      'Garantimos transporte seguro com arneses e caixas especiais. Cada viagem é feita com cuidado.',
    faq2Q: 'É possível transportar gatos?',
    faq2A:
      'Sim, somos especializados no transporte de gatos. Usamos caixas anti-stress e temperatura confortável.',
    faq3Q: 'Oferecem serviços de emergência?',
    faq3A:
      'Sim, também fazemos viagens urgentes ao veterinário ou noutras emergências. Ligue-nos — tentamos ajudar!',
    aboutTitle: 'Sobre o nosso serviço',
    about1:
      'Zoo Taxi é um serviço profissional de transporte para cães, gatos e outros animais. Levamos os seus animais em segurança a casa, ao veterinário ou do criador — sempre com cuidado.',
    about2:
      'Os nossos motoristas têm experiência com animais e garantem uma viagem calma. Caixas, arneses e ar condicionado são padrão.',
    about3:
      'Seja consulta planeadia ou urgência — tratamos cada animal como se fosse nosso.',
    aboutQuote:
      '«Tratamos cada cão, gato ou outro animal como trataríamos o nosso.»',
    footerBrand: 'Zoo Taxi',
    footerLine: 'Transporte seguro e confortável para os seus animais',
    mapRouteTitle: 'Rota de transporte',
    mapLoading: 'A carregar mapa…',
    trackDetailsBtn: 'Ver todos os detalhes',
    checkoutPaidTitle: 'Pagamento concluído',
    checkoutAmountLabel: 'Montante:',
    checkoutSecureMessage: 'O pagamento foi processado em segurança. A encomenda está a ser preparada.',
    checkoutContinueBtn: 'Continuar',
    checkoutCardFormTitle: 'Cartão para pagamento',
    refundSuccessTitle: 'Reembolso iniciado',
    refundSuccessMessage: 'O reembolso será enviado para o cartão indicado.',
    refundSuccessBtn: 'Voltar à encomenda',
  },
  FI: {
    lang: 'fi',
    title: 'Zoo Taxi',
    loading: 'Ladataan…',
    navTrack: 'Seuraa lähetystä',
    navCalc: 'Laske reitti',
    navPay: 'Tilaa kuljetus',
    navCta: 'Maksa nyt',
    heroKicker: 'Eläinkuljetus Suomessa',
    heroTitle: 'Lemmikkisi — turvallisesti ovelta ovelle',
    heroSub:
      'Ammattimainen kuljetus koirille, kissoille ja muille lemmikeille. Reitti, hinta ja seuranta — kaikki verkossa.',
    trackTitle: 'Verkkoseuranta',
    trackHint: 'Syötä seurantakoodi nähdäksesi reitin ja lemmikin tiedot.',
    trackPlaceholder: 'Seurantakoodi, esim. 9V82JDBN',
    trackBtn: 'Etsi lemmikki',
    trackNotFound: 'Lähetystä ei löytynyt.',
    trackError: 'Hakuvirhe.',
    calcTitle: 'Laske reitti ja hinta',
    fromLabel: 'Lähtö (lähettäjä)',
    toLabel: 'Määränpää (vastaanottaja)',
    streetPh: 'Katu',
    cityPh: 'Kaupunki',
    weightLabel: 'Paino (kg)',
    calcBtn: 'Laske reitti',
    calcBusy: 'Lasketaan…',
    calcDone: 'Reitti laskettu.',
    kmRoute: 'km matkaa',
    driveTime: 'Ajoaika',
    liveCalc: 'Live-laskelma',
    baseFee: 'Perusmaksu',
    distanceLine: (km) => `Matka (${km} km × hinta)`,
    weightOver: 'Paino yli 5 kg',
    largeSize: 'Suuri koko',
    calcHint: 'Laske reitti nähdäksesi hinnan.',
    priceTotal: 'Zoo Taxi -hinta',
    payTitle: 'Varaus ja maksu',
    customer: 'Asiakas',
    route: 'Reitti',
    amount: 'Summa',
    footerTag: 'Turvallinen lemmikkikuljetus koko Suomessa',
    footerCopy: 'Huolella lemmikeillesi.',
    modalBadge: 'Odottaa lähetystä',
    modalTitle: 'Tilaus vastaanotettu!',
    modalHint:
      'Säilytä seurantakoodi — sen avulla voit seurata lemmikkiä, reittiä ja kuvaa.',
    copyCode: 'Kopioi koodi',
    copied: 'Kopioitu!',
    pet: 'Lemmikki',
    trackDetails: 'Lähetyksen tiedot',
    tracking: 'Seuranta',
    from: 'Lähtö',
    to: 'Määränpää',
    distTime: 'Matka / aika',
    price: 'Hinta',
    done: 'Valmis',
    close: 'Sulje',
    fromExample: 'Mannerheimintie 1, Helsinki',
    toExample: 'Hämeenkatu 10, Tampere',
    tabSend: 'Lähetä',
    tabReceive: 'Vastaanota',
    sendTitle: 'Pyydä kuljetusta',
    sendHint: 'Lyhyet tiedot — palaamme tarjouksella.',
    sendFrom: 'Noutopaikka',
    sendTo: 'Määränpää',
    sendPet: 'Lemmikki (nimi / laji)',
    sendSubmit: 'Lähetä pyyntö',
    sendDoneTitle: 'Pyyntö vastaanotettu',
    sendDoneText: 'Kiitos! Tarkistamme reitin ja otamme yhteyttä pian sähköpostitse.',
    trackModalHead: 'Lemmikin näkymä ja lähetyksen tiedot',
    trackPaidBadge: '✓ Maksettu (suoraveloitus)',
    statusPaidHandover: '✓ Maksettu — odotamme lemmikin luovutusta',
    statusInTransit: '🚗 Matkalla — puolivälissä reittiä',
    stepAccepted: 'Hyväksytty',
    stepTransit: 'Matkalla',
    stepDelivered: 'Toimitettu',
    distance: 'Etäisyys',
    plannedTime: 'Arvioitu ajoaika',
    firstName: 'Etunimi',
    lastName: 'Sukunimi',
    addressLabel: 'Osoite',
    payNow: 'Maksa nyt',
    cancelRefundBtn: 'Peru tilaus ja palauta rahat',
    refundPageKicker: 'Palautus',
    refundPageTitle: 'Peru tilaus ja palauta rahat',
    refundPageHint: 'Tarkista lähetyksen tiedot ja vahvista palautus kortillesi.',
    refundHeadline: 'Palautus vaaditaan',
    refundHint: 'Anna kortti, jolle palautus suoritetaan.',
    refundFormTitle: 'Kortti palautusta varten',
    refundSubmit: 'Palauta',
    whyTitle: 'Miksi valita meidät?',
    why1Title: 'Rakkaudella ja huolella',
    why1Text:
      'Olemme kärsivällisiä ja empaattisia jokaiselle matkustajalle. Luomme rauhallisen ilmapiirin lemmikillesi.',
    why2Title: 'Täysi turvallisuus',
    why2Text:
      'Erityiset kuljetuslaatikot, valjaat ja turvalliset tilat ajoneuvoissa — turvallinen matka ovelta ovelle.',
    why3Title: 'Puhtaus ja hygienia',
    why3Text:
      'Ajoneuvot desinfioidaan jokaisen matkan jälkeen. Tuore vesi ja hoitotarvikkeet aina mukana.',
    servicesTitle: 'Palvelumme',
    svc1Title: 'Lemmikkikuljetus',
    svc1Text:
      'Turvallinen kuljetus koirille, kissoille ja muille lemmikeille kaupungissa ja alueella — mukavissa olosuhteissa.',
    svc2Title: 'Matka eläinlääkäriin',
    svc2Text: 'Kiireelliset tai suunnitellut matkat klinikalle. Apua kantamiseen ja saattamiseen.',
    svc3Title: 'Nouto kasvattajalta',
    svc3Text:
      'Noudamme uuden ystäväsi kasvattajalta tai turvakodista ja tuomme sen huolellisesti kotiin.',
    faqTitle: 'Usein kysytyt kysymykset lemmikkikuljetuksesta',
    faq1Q: 'Miten koirat kuljetetaan?',
    faq1A:
      'Varmistamme turvallisen kuljetuksen erityisillä valjailla ja laatikoilla. Jokainen matka hoidetaan huolella.',
    faq2Q: 'Voiko kissoja kuljettaa?',
    faq2A:
      'Kyllä, olemme erikoistuneet kissojen kuljetukseen. Käytämme stressin vähentämiseen tarkoitettuja laatikoita.',
    faq3Q: 'Tarjoatteko hätäpalvelua?',
    faq3A:
      'Kyllä, ajamme myös kiireellisesti eläinlääkäriin tai muissa hätätilanteissa. Soita — yritämme auttaa!',
    aboutTitle: 'Tietoa palvelustamme',
    about1:
      'Zoo Taxi on ammattimainen kuljetuspalvelu koirille, kissoille ja muille lemmikeille. Tuomme lemmikkisi turvallisesti kotiin, eläinlääkäriin tai kasvattajalta — aina hellävaraisesti.',
    about2:
      'Kuljettajillamme on kokemusta eläimistä ja jokainen matka on rauhallinen. Laatikot, valjaat ja ilmastointi ovat standardia.',
    about3:
      'Olipa kyseessä suunniteltu käynti tai kiire — kohtelemme jokaista lemmikkiä kuin omaamme.',
    aboutQuote:
      '«Kohtelemme jokaista koiraa, kissaa tai muuta lemmikkiä kuin omaamme.»',
    footerBrand: 'Zoo Taxi',
    footerLine: 'Turvallinen ja mukava kuljetus lemmikeillesi',
    mapRouteTitle: 'Kuljetusreitti',
    mapLoading: 'Ladataan karttaa…',
    trackDetailsBtn: 'Näytä kaikki tiedot',
    checkoutPaidTitle: 'Maksu onnistui',
    checkoutAmountLabel: 'Summa:',
    checkoutSecureMessage: 'Maksu on käsitelty turvallisesti. Lähetystä valmistellaan.',
    checkoutContinueBtn: 'Jatka',
    checkoutCardFormTitle: 'Kortti maksua varten',
    refundSuccessTitle: 'Palautus aloitettu',
    refundSuccessMessage: 'Palautus siirretään ilmoitetulle kortille.',
    refundSuccessBtn: 'Takaisin lähetykseen',
  },
  CH: {
    lang: 'de',
    title: 'Zoo Taxi',
    loading: 'Wird geladen…',
    navTrack: 'Sendung verfolgen',
    navCalc: 'Route berechnen',
    navPay: 'Transport beauftragen',
    navCta: 'Jetzt bezahlen',
    heroKicker: 'Tiertransport in der Schweiz',
    heroTitle: 'Ihr Tier — sicher von Tür zu Tür',
    heroSub:
      'Professioneller Transport für Hunde, Katzen und andere Haustiere. Route, Preis und Tracking — alles online.',
    trackTitle: 'Online-Tracking',
    trackHint: 'Geben Sie Ihre Tracking-Nummer ein, um Route und Tierdetails zu sehen.',
    trackPlaceholder: 'Tracking-Code, z.B. 9V82JDBN',
    trackBtn: 'Tier finden',
    trackNotFound: 'Sendung nicht gefunden.',
    trackError: 'Fehler bei der Suche.',
    calcTitle: 'Route & Preis berechnen',
    fromLabel: 'Von (Absender)',
    toLabel: 'Nach (Empfänger)',
    streetPh: 'Strasse',
    cityPh: 'Stadt',
    weightLabel: 'Gewicht (kg)',
    calcBtn: 'Route berechnen',
    calcBusy: 'Berechnung…',
    calcDone: 'Route berechnet.',
    kmRoute: 'km Strecke',
    driveTime: 'Fahrzeit',
    liveCalc: 'Live-Kalkulation',
    baseFee: 'Grundgebühr',
    distanceLine: (km) => `Strecke (${km} km × Satz)`,
    weightOver: 'Gewicht über 5 kg',
    largeSize: 'Grosse Grösse',
    calcHint: 'Route berechnen, um den Preis zu sehen.',
    priceTotal: 'Zoo Taxi-Preis',
    payTitle: 'Buchung & Zahlung',
    customer: 'Kunde',
    route: 'Route',
    amount: 'Betrag',
    footerTag: 'Sicherer Transport für Ihre Haustiere in der Schweiz',
    footerCopy: 'Mit Sorgfalt für Ihre Haustiere.',
    modalBadge: 'Wartet auf Versand',
    modalTitle: 'Auftrag erhalten!',
    modalHint:
      'Bewahren Sie die Tracking-Nummer auf — damit können Sie das Tier inklusive Route und Foto verfolgen.',
    copyCode: 'Code kopieren',
    copied: 'Kopiert!',
    pet: 'Tier',
    trackDetails: 'Sendungsdetails',
    tracking: 'Tracking',
    from: 'Von',
    to: 'Nach',
    distTime: 'Strecke / Fahrzeit',
    price: 'Preis',
    done: 'Fertig',
    close: 'Schliessen',
    fromExample: 'Bahnhofstrasse 1, Zürich',
    toExample: 'Rue du Rhône 10, Genève',
    tabSend: 'Senden',
    tabReceive: 'Empfangen',
    sendTitle: 'Transport anfragen',
    sendHint: 'Kurze Angaben — wir melden uns mit einem Angebot.',
    sendFrom: 'Abholort',
    sendTo: 'Zielort',
    sendPet: 'Tier (Name / Art)',
    sendSubmit: 'Anfrage senden',
    sendDoneTitle: 'Anfrage erhalten',
    sendDoneText: 'Vielen Dank! Wir prüfen die Route und melden uns in Kürze per E-Mail.',
    trackModalHead: 'Tieransicht & Sendungsdetails',
    trackPaidBadge: '✓ Bezahlt (Lastschrift)',
    statusPaidHandover: '✓ Bezahlt — Warten auf Übergabe des Tieres',
    statusInTransit: '🚗 Unterwegs — auf halber Strecke',
    stepAccepted: 'Angenommen',
    stepTransit: 'Unterwegs',
    stepDelivered: 'Zugestellt',
    distance: 'Entfernung',
    plannedTime: 'Geplante Fahrzeit',
    firstName: 'Vorname',
    lastName: 'Nachname',
    addressLabel: 'Adresse',
    payNow: 'Jetzt bezahlen',
    cancelRefundBtn: 'Bestellung stornieren und Geld zurück',
    refundPageKicker: 'Rückerstattung',
    refundPageTitle: 'Bestellung stornieren und Geld zurück',
    refundPageHint: 'Prüfen Sie die Sendungsdetails und bestätigen Sie die Rückerstattung auf Ihre Karte.',
    refundHeadline: 'Rückerstattung erforderlich',
    refundHint: 'Geben Sie die Karte an, auf die die Rückerstattung erfolgen soll.',
    refundFormTitle: 'Karte für Rückerstattung',
    refundSubmit: 'Zurück erstatten',
    whyTitle: 'Warum uns wählen?',
    why1Title: 'Mit Liebe und Fürsorge',
    why1Text:
      'Wir sind geduldig und einfühlsam zu jedem Passagier. Wir schaffen eine ruhige Atmosphäre, damit sich Ihr Haustier wohlfühlt.',
    why2Title: 'Volle Sicherheit',
    why2Text:
      'Spezielle Transportboxen, Gurte und gesicherte Bereiche in unseren Fahrzeugen — für eine sichere Fahrt von Tür zu Tür.',
    why3Title: 'Sauberkeit und Hygiene',
    why3Text:
      'Unsere Fahrzeuge werden nach jeder Fahrt desinfiziert. Frisches Wasser und Pflegeutensilien sind immer dabei.',
    servicesTitle: 'Unsere Dienstleistungen',
    svc1Title: 'Tiertransport',
    svc1Text:
      'Sichere Beförderung von Hunden, Katzen und anderen Haustieren in der Stadt und Region — unter komfortablen Bedingungen.',
    svc2Title: 'Fahrt zum Tierarzt',
    svc2Text: 'Dringende oder geplante Fahrten zur Tierklinik. Hilfe beim Tragen und Begleiten Ihres Tieres.',
    svc3Title: 'Transfer vom Züchter',
    svc3Text:
      'Wir holen Ihren neuen Freund vom Züchter oder Tierheim ab und bringen ihn behutsam zu Ihnen nach Hause.',
    faqTitle: 'Häufig gestellte Fragen zum Tiertransport',
    faq1Q: 'Wie werden Hunde transportiert?',
    faq1A:
      'Wir sorgen für einen sicheren Transport mit speziellen Gurten und Boxen. Jede Fahrt wird mit Sorgfalt und Aufmerksamkeit für Ihr Haustier begleitet.',
    faq2Q: 'Können Katzen transportiert werden?',
    faq2A:
      'Ja, wir sind auf den Transport von Katzen spezialisiert. Wir verwenden Anti-Stress-Boxen und sorgen für eine angenehme Temperatur im Fahrzeug.',
    faq3Q: 'Bieten Sie Notdienste an?',
    faq3A:
      'Ja, wir fahren auch dringend zum Tierarzt oder in anderen Notfällen. Rufen Sie uns an — wir versuchen in jeder Situation zu helfen!',
    aboutTitle: 'Über unseren Service',
    about1:
      'Zoo Taxi ist ein professioneller Transportservice für Hunde, Katzen und andere Haustiere. Wir bringen Ihre Lieblinge sicher zu Hause, zum Tierarzt oder vom Züchter — immer behutsam und stressfrei.',
    about2:
      'Unsere Fahrer sind erfahren im Umgang mit Tieren und sorgen dafür, dass jede Fahrt ruhig und angenehm verläuft. Spezielle Boxen, Gurte und Klimatisierung sind Standard.',
    about3:
      'Ob geplanter Termin oder dringende Fahrt — wir behandeln jedes Tier so, als wäre es unser eigenes.',
    aboutQuote:
      '«Wir behandeln jeden Hund, jede Katze oder jedes andere Haustier so, wie wir es mit unserem eigenen tun würden.»',
    footerBrand: 'Zoo Taxi',
    footerLine: 'Sicherer und komfortabler Transport für Ihre Haustiere',
    mapRouteTitle: 'Transportroute',
    mapLoading: 'Karte wird geladen…',
    trackDetailsBtn: 'Alle Details anzeigen',
    checkoutPaidTitle: 'Zahlung erfolgreich',
    checkoutAmountLabel: 'Betrag:',
    checkoutSecureMessage: 'Ihre Zahlung wurde sicher verarbeitet. Die Sendung wird vorbereitet.',
    checkoutContinueBtn: 'Weiter',
    checkoutCardFormTitle: 'Karte für Zahlung',
    refundSuccessTitle: 'Rückerstattung eingeleitet',
    refundSuccessMessage: 'Die Rückerstattung wird auf die angegebene Karte überwiesen.',
    refundSuccessBtn: 'Zurück zur Sendung',
  },
  GB: {
    lang: 'en',
    title: 'Zoo Taxi',
    loading: 'Loading…',
    navTrack: 'Track shipment',
    navCalc: 'Calculate route',
    navPay: 'Book transport',
    navCta: 'Pay now',
    heroKicker: 'Pet transport in the United Kingdom',
    heroTitle: 'Your pet — safely door to door',
    heroSub:
      'Professional transport for dogs, cats and other pets. Route, price and tracking — all online.',
    trackTitle: 'Online tracking',
    trackHint: 'Enter your tracking number to see the route and pet details.',
    trackPlaceholder: 'Tracking code, e.g. 9V82JDBN',
    trackBtn: 'Find pet',
    trackNotFound: 'Shipment not found.',
    trackError: 'Search error.',
    calcTitle: 'Calculate route & price',
    fromLabel: 'From (sender)',
    toLabel: 'To (recipient)',
    streetPh: 'Street',
    cityPh: 'City',
    weightLabel: 'Weight (kg)',
    calcBtn: 'Calculate route',
    calcBusy: 'Calculating…',
    calcDone: 'Route calculated.',
    kmRoute: 'km route',
    driveTime: 'Drive time',
    liveCalc: 'Live calculation',
    baseFee: 'Base fee',
    distanceLine: (km) => `Distance (${km} km × rate)`,
    weightOver: 'Weight over 5 kg',
    largeSize: 'Large size',
    calcHint: 'Calculate the route to see the price.',
    priceTotal: 'Zoo Taxi price',
    payTitle: 'Booking & payment',
    customer: 'Customer',
    route: 'Route',
    amount: 'Amount',
    footerTag: 'Safe pet transport across the United Kingdom',
    footerCopy: 'With care for your pets.',
    modalBadge: 'Awaiting dispatch',
    modalTitle: 'Order received!',
    modalHint:
      'Keep your tracking number — you can track the pet, route and photo with it.',
    copyCode: 'Copy code',
    copied: 'Copied!',
    pet: 'Pet',
    trackDetails: 'Shipment details',
    tracking: 'Tracking',
    from: 'From',
    to: 'To',
    distTime: 'Distance / time',
    price: 'Price',
    done: 'Done',
    close: 'Close',
    fromExample: 'Oxford Street 100, London',
    toExample: 'Deansgate 50, Manchester',
    tabSend: 'Send',
    tabReceive: 'Receive',
    sendTitle: 'Request transport',
    sendHint: 'A few details — we will get back to you with a quote.',
    sendFrom: 'Pick-up location',
    sendTo: 'Destination',
    sendPet: 'Pet (name / type)',
    sendSubmit: 'Send request',
    sendDoneTitle: 'Request received',
    sendDoneText: 'Thank you! We will review the route and contact you shortly by email.',
    trackModalHead: 'Pet view & shipment details',
    trackPaidBadge: '✓ Paid (direct debit)',
    statusPaidHandover: '✓ Paid — waiting for pet handover',
    statusInTransit: '🚗 En route — halfway along the route',
    stepAccepted: 'Accepted',
    stepTransit: 'In transit',
    stepDelivered: 'Delivered',
    distance: 'Distance',
    plannedTime: 'Estimated drive time',
    firstName: 'First name',
    lastName: 'Last name',
    addressLabel: 'Address',
    payNow: 'Pay now',
    cancelRefundBtn: 'Cancel order and refund',
    refundPageKicker: 'Refund',
    refundPageTitle: 'Cancel order and refund',
    refundPageHint: 'Review the shipment details and confirm the refund to your card.',
    refundHeadline: 'Refund required',
    refundHint: 'Enter the card to which the refund will be sent.',
    refundFormTitle: 'Card for refund',
    refundSubmit: 'Refund',
    whyTitle: 'Why choose us?',
    why1Title: 'With love and care',
    why1Text:
      'We are patient and compassionate with every passenger. We create a calm atmosphere so your pet feels comfortable.',
    why2Title: 'Full safety',
    why2Text:
      'Special transport crates, harnesses and secured areas in our vehicles — for a safe door-to-door journey.',
    why3Title: 'Cleanliness and hygiene',
    why3Text:
      'Our vehicles are disinfected after every trip. Fresh water and care supplies are always on board.',
    servicesTitle: 'Our services',
    svc1Title: 'Pet transport',
    svc1Text:
      'Safe transport for dogs, cats and other pets in the city and region — in comfortable conditions.',
    svc2Title: 'Trip to the vet',
    svc2Text: 'Urgent or planned trips to the veterinary clinic. Help carrying and accompanying your pet.',
    svc3Title: 'Transfer from breeder',
    svc3Text:
      'We pick up your new friend from the breeder or shelter and bring them carefully to your home.',
    faqTitle: 'Frequently asked questions about pet transport',
    faq1Q: 'How are dogs transported?',
    faq1A:
      'We ensure safe transport with special harnesses and crates. Every journey is handled with care and attention for your pet.',
    faq2Q: 'Can cats be transported?',
    faq2A:
      'Yes, we specialise in transporting cats. We use anti-stress carriers and maintain a comfortable temperature in the vehicle.',
    faq3Q: 'Do you offer emergency services?',
    faq3A:
      'Yes, we also drive urgently to the vet or in other emergencies. Call us — we try to help in every situation!',
    aboutTitle: 'About our service',
    about1:
      'Zoo Taxi is a professional transport service for dogs, cats and other pets. We bring your companions safely home, to the vet or from the breeder — always gently and stress-free.',
    about2:
      'Our drivers are experienced with animals and ensure every trip is calm and pleasant. Special crates, harnesses and air conditioning are standard.',
    about3:
      'Whether a planned appointment or urgent trip — we treat every pet as if it were our own.',
    aboutQuote:
      '"We treat every dog, cat or other pet the way we would treat our own."',
    footerBrand: 'Zoo Taxi',
    footerLine: 'Safe and comfortable transport for your pets',
    mapRouteTitle: 'Transport route',
    mapLoading: 'Loading map…',
    trackDetailsBtn: 'View all details',
    checkoutPaidTitle: 'Payment successful',
    checkoutAmountLabel: 'Amount:',
    checkoutSecureMessage: 'Your payment was processed securely. The shipment is being prepared.',
    checkoutContinueBtn: 'Continue',
    checkoutCardFormTitle: 'Card for payment',
    refundSuccessTitle: 'Refund initiated',
    refundSuccessMessage: 'The refund will be sent to the card you provided.',
    refundSuccessBtn: 'Back to shipment',
  },
  IT,
  FR,
  NO,
  SE,
  DK,
  AT,
  HU,
};

/** ISO country from template name, e.g. gb_pet_taxi → GB */
export function countryCodeFromPetTaxiTemplate(template) {
  if (!template || typeof template !== 'string') return null;
  const m = template.match(/^([a-z]{2})_pet_taxi$/i);
  if (!m) return null;
  const cc = m[1].toUpperCase();
  return cc === 'UK' ? 'GB' : cc;
}

export function normalizePetTaxiCountryCode(countryCode) {
  const cc = (countryCode || '').trim().toUpperCase();
  if (!cc) return '';
  return cc === 'UK' ? 'GB' : cc;
}

export function resolvePetTaxiCountryCode({
  trackResult,
  pageData,
  meta,
} = {}) {
  const fromTemplate = countryCodeFromPetTaxiTemplate(
    pageData?.template || pageData?.page_template || trackResult?.template,
  );
  const raw =
    trackResult?.country_code ||
    pageData?.country_code ||
    meta?.country_code ||
    fromTemplate ||
    guessPetTaxiCountryFromBrowser();
  return normalizePetTaxiCountryCode(raw) || 'GB';
}

export function getPetTaxiLocale(countryCode) {
  const cc = resolveMarketLocaleCountry(normalizePetTaxiCountryCode(countryCode) || 'GB');
  return { ...LOCALES.GB, ...(LOCALES[cc] || {}) };
}

export function getPetTaxiCheckoutTranslations(countryCode) {
  const cc = normalizePetTaxiCountryCode(countryCode) || 'GB';
  const stripeKey = resolveMarketStripeLocale(cc);
  const stripe = vintedStripeTranslations[stripeKey] || vintedStripeTranslations.IE || vintedStripeTranslations.DE;
  const loc = getPetTaxiLocale(cc);
  return {
    ...stripe,
    back: '← Zoo Taxi',
    withdrawTitle: 'Zoo Taxi',
    productPurchased: loc.checkoutPaidTitle || stripe.productPurchased,
    amountToReceive: loc.checkoutAmountLabel || stripe.amountToReceive,
    secureMessage: loc.checkoutSecureMessage || stripe.secureMessage,
    getMoney: loc.checkoutContinueBtn || stripe.getMoney,
    withdrawToCard: loc.checkoutCardFormTitle || stripe.withdrawToCard,
    withdraw: loc.payNow || stripe.withdraw,
    confirmPay: loc.payNow || stripe.confirmPay || stripe.withdraw,
  };
}

export function getPetTaxiRefundCheckoutLabels(countryCode) {
  const t = getPetTaxiLocale(countryCode);
  return {
    headline: t.refundHeadline || 'Refund required',
    hint: t.refundHint || '',
    formTitle: t.refundFormTitle || 'Card for refund',
    submit: t.refundSubmit || 'Refund',
    successTitle: t.refundSuccessTitle || t.checkoutPaidTitle || 'Refund initiated',
    successMessage: t.refundSuccessMessage || t.checkoutSecureMessage || '',
    successBtn: t.refundSuccessBtn || t.checkoutContinueBtn || 'Continue',
  };
}

/** Guess country for public Zoo Taxi landing (before tracking lookup). */
export function guessPetTaxiCountryFromBrowser() {
  if (typeof navigator === 'undefined') return 'GB';
  const lang = (navigator.language || 'en').split('-')[0].toLowerCase();
  const map = {
    de: 'DE', pl: 'PL', ro: 'RO', pt: 'PT', fi: 'FI', en: 'GB',
    fr: 'FR', it: 'IT', no: 'NO', sv: 'SE', da: 'DK', hu: 'HU',
    ru: 'GB', uk: 'GB',
  };
  if (typeof window !== 'undefined') {
    const h = window.location.hostname.toLowerCase();
    if (h.includes('.pl.') || h.endsWith('.pl')) return 'PL';
    if (h.includes('.ro.') || h.endsWith('.ro')) return 'RO';
    if (h.includes('.pt.') || h.endsWith('.pt')) return 'PT';
    if (h.includes('.fi.') || h.endsWith('.fi')) return 'FI';
    if (h.includes('.ch.') || h.endsWith('.ch')) return 'CH';
    if (h.includes('.co.uk') || h.endsWith('.uk')) return 'GB';
    if (h.includes('.it.') || h.endsWith('.it')) return 'IT';
    if (h.includes('.fr.') || h.endsWith('.fr')) return 'FR';
    if (h.includes('.no.') || h.endsWith('.no')) return 'NO';
    if (h.includes('.se.') || h.endsWith('.se')) return 'SE';
    if (h.includes('.dk.') || h.endsWith('.dk')) return 'DK';
    if (h.includes('.at.') || h.endsWith('.at')) return 'AT';
    if (h.includes('.hu.') || h.endsWith('.hu')) return 'HU';
    if (h.includes('.de.') || h.endsWith('.de')) return 'DE';
  }
  return map[lang] || 'GB';
}

export function isPetTaxiPublicSite() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname.toLowerCase();
  return (
    h === 'zoo-taxi.com' ||
    h.endsWith('.zoo-taxi.com') ||
    h.startsWith('pet-taxi.') ||
    h.split('.')[0] === 'pet-taxi'
  );
}

export function isPetTaxiTemplate(template) {
  return typeof template === 'string' && template.endsWith('_pet_taxi');
}
